// API clients for the candidatures (public questionnaire of a coach).
//   publicCandidatureApi -> /api/public/candidatures (no account, page /q/:handle/:slug)
//   candidaturesApi      -> /api/candidatures (coach: form + prospects)
import { getApiBaseUrlWithApi } from '../config/api';

export class CandidaturesApiError extends Error {
  constructor(message, { status, code, errors } = {}) {
    super(message);
    this.name = 'CandidaturesApiError';
    this.status = status;
    this.code = code;
    // Field / question error codes of a rejected submission ({ [field]: code }).
    this.errors = errors;
  }
}

const request = async (url, options = {}, { auth = true } = {}) => {
  const token = auth ? localStorage.getItem('authToken') : null;
  const res = await fetch(url, {
    ...options,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (res.status === 204) return null;
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new CandidaturesApiError(body?.message || body?.error || `Request failed (${res.status})`, {
      status: res.status,
      code: body?.code,
      errors: body?.errors,
    });
  }
  return body?.data;
};

const json = (method, payload) => ({ method, body: JSON.stringify(payload ?? {}) });
// /q/<handle>/<slug>; /q/<handle> alone = the coach's first open form.
const publicUrl = (handle, slug, path = '') => `${getApiBaseUrlWithApi()}/public/candidatures/${encodeURIComponent(handle)}${slug ? `/${encodeURIComponent(slug)}` : ''}${path}`;
const coachUrl = (path) => `${getApiBaseUrlWithApi()}/candidatures${path}`;

export const publicCandidatureApi = {
  // { handle, slug, coachName, title, description, questions, hasPayment }
  get: (handle, slug) => request(publicUrl(handle, slug), {}, { auth: false }),
  // -> { prospectId, paymentUrl }
  submit: (handle, slug, payload) => request(publicUrl(handle, slug), json('POST', payload), { auth: false }),
  // "Prendre le coaching" clicked: shown to the coach. keepalive because the
  // page is being left for the payment link; no header and no body, so no CORS
  // preflight (not allowed with keepalive in every browser). Fire and forget.
  paymentClick: (handle, prospectId) => fetch(
    publicUrl(handle, null, `/prospects/${encodeURIComponent(prospectId)}/payment-click`),
    { method: 'POST', keepalive: true }
  ).catch(() => {}),
};

export const candidaturesApi = {
  // { handle, forms } (oldest first); before the first form, handle null and
  // { suggestedHandle }
  listForms: () => request(coachUrl('/forms')),
  // The coach's identifier in every link (/q/<handle>/…) -> { handle }
  saveHandle: (handle) => request(coachUrl('/handle'), json('PUT', { handle })),
  // { slug?, title?, description?, questions?, isOpen? } -> { form, handle }
  createForm: (payload) => request(coachUrl('/forms'), json('POST', payload)),
  // { slug?, title?, description?, questions?, paymentUrl?, isOpen? } -> { form }
  updateForm: (id, payload) => request(coachUrl(`/forms/${id}`), json('PUT', payload)),
  deleteForm: (id) => request(coachUrl(`/forms/${id}`), { method: 'DELETE' }),
  // Prospects with `submissions` (every form they answered, newest first);
  // with formId, only those who answered that form.
  listProspects: ({ archived = false, formId = null } = {}) => {
    const params = new URLSearchParams();
    if (archived) params.set('archived', 'true');
    if (formId) params.set('formId', formId);
    const qs = params.toString();
    return request(coachUrl(`/prospects${qs ? `?${qs}` : ''}`));
  },
  summary: () => request(coachUrl('/prospects/summary')),
  updateProspect: (id, status) => request(coachUrl(`/prospects/${id}`), json('PATCH', { status })),
  deleteProspect: (id) => request(coachUrl(`/prospects/${id}`), { method: 'DELETE' }),
};

// Payment link of the end screen (« Prendre le coaching »): hidden for now —
// a bare PayPal / Lydia link tells the prospect neither the amount nor what
// they pay for. The server still stores it, so it can come back.
export const PAYMENT_LINK_ENABLED = false;

/** Public address of a coach's form (/q/<handle>/<slug>), or of the coach alone. */
export const candidatureUrl = (handle, slug) => `${window.location.origin}/q/${handle}${slug ? `/${slug}` : ''}`;
